import React, { useState, useEffect, useRef } from "react";
import { unAuthorizedPost } from "services";
import { FETCH_A_GAME_DETAILS } from "graphql";
import { Card, Grid, Stack, Chip, Box, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import PageRouteNavigator from "components/pageRouteNavigator";
import Loader from "components/Loader";
import Avatar from "react-avatar";
import { avatars } from "./Organisers";
import { listAllCalledNumbers } from "api/list-all-called-numbers";

const isWinningLine = (arr) => arr.every((i) => i.includes("marked"));
const BINGO = ["B", "I", "N", "G", "O"];

// Enhanced Ad Pop-up Component with YouTube Support - Fixed Mute Button
const AdPopup = ({ ad, onClose, duration = 25 }) => {
  const [remainingTime, setRemainingTime] = useState(duration);
  const [mediaError, setMediaError] = useState(false);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // Start muted by default
  const videoRef = useRef(null);
  const iframeRef = useRef(null);

  // Debug: Log the ad data to console
  useEffect(() => {
    console.log("Ad data:", ad);
    console.log("Media URL:", ad.media_url);
  }, [ad]);

  useEffect(() => {
    // Countdown timer
    const interval = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [onClose]);

  const handleAdClick = (e) => {
    e.stopPropagation();
    if (ad.link) {
      window.open(ad.link, "_blank");
    }
  };

  // Helper function to determine if media is video
  const isVideo = (url) => {
    if (!url) return false;
    const videoExtensions = [
      ".mp4",
      ".webm",
      ".ogg",
      ".avi",
      ".mov",
      ".wmv",
      ".flv",
      ".mkv",
    ];
    const lowerUrl = url.toLowerCase();
    return (
      videoExtensions.some((ext) => lowerUrl.includes(ext)) ||
      lowerUrl.includes("video") ||
      lowerUrl.includes(".m3u8")
    ); // for HLS streams
  };

  // Helper function to check if URL is YouTube
  const isYouTubeUrl = (url) => {
    if (!url) return false;
    return url.includes("youtube.com") || url.includes("youtu.be");
  };

  // Helper function to extract YouTube video ID
  const getYouTubeVideoId = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Helper function to get YouTube embed URL
  const getYouTubeEmbedUrl = (url) => {
    const videoId = getYouTubeVideoId(url);
    return videoId
      ? `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${
          isMuted ? 1 : 0
        }&controls=0&modestbranding=1&rel=0&enablejsapi=1`
      : null;
  };

  const toggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);

    // Handle regular video element
    if (videoRef.current) {
      videoRef.current.muted = newMutedState;
    }

    // Handle YouTube iframe - use postMessage API instead of reloading
    if (iframeRef.current && isYouTubeUrl(ad.media_url)) {
      try {
        const command = newMutedState ? 'mute' : 'unMute';
        iframeRef.current.contentWindow.postMessage(
          `{"event":"command","func":"${command}","args":""}`,
          '*'
        );
      } catch (error) {
        console.warn('YouTube postMessage failed, falling back to src reload:', error);
        // Fallback: reload iframe with new mute parameter
        const newSrc = getYouTubeEmbedUrl(ad.media_url).replace(
          `mute=${isMuted ? 1 : 0}`,
          `mute=${newMutedState ? 1 : 0}`
        );
        iframeRef.current.src = newSrc;
      }
    }
  };

  const handleMediaError = (e) => {
    console.error("Media failed to load:", e);
    console.error("Failed URL:", ad.media_url);
    setMediaError(true);
  };

  const handleMediaLoad = () => {
    console.log("Media loaded successfully");
    setMediaLoaded(true);
    setMediaError(false);
    
    // Ensure video starts muted
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  };

  const progressPercentage = ((duration - remainingTime) / duration) * 100;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        animation: "fadeIn 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        "@keyframes fadeIn": {
          from: { opacity: 0, backdropFilter: "blur(0px)" },
          to: { opacity: 1, backdropFilter: "blur(8px)" },
        },
      }}
      onClick={onClose}
    >
      <Card
        sx={{
          p: 0,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: 3,
          position: "relative",
          cursor: "pointer",
          maxWidth: "90vw",
          maxHeight: "90vh",
          overflow: "hidden",
          boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
          transition: "all 0.3s ease",
          animation: "slideUp 0.5s ease-out",
          "&:hover": {
            transform: "scale(1.02)",
            boxShadow: "0 25px 50px rgba(0,0,0,0.4)",
          },
          "@keyframes slideUp": {
            from: {
              opacity: 0,
              transform: "translateY(50px)",
            },
            to: {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        }}
        onClick={handleAdClick}
      >
        {/* Progress Bar */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "4px",
            width: `${progressPercentage}%`,
            background: "linear-gradient(90deg, #ff6b35, #ffd700)",
            transition: "width 1s ease",
            borderRadius: "0 0 2px 0",
          }}
        />

        {/* Header with Sponsored, Mute Button and Timer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 4,
            pt: 3,
            pb: 1,
          }}
        >
          <Box
            sx={{
              background: "#ff6b35",
              color: "white",
              px: 3,
              py: 1,
              borderRadius: 2,
              fontSize: "12px",
              fontWeight: "bold",
              letterSpacing: "1px",
            }}
          >
            SPONSORED
          </Box>

          {/* Mute Button */}
          <Box
            onClick={(e) => {
              e.stopPropagation();
              toggleMute();
            }}
            sx={{
              background: isMuted
                ? "rgba(255,69,0,0.8)"
                : "rgba(76,175,80,0.8)",
              color: "white",
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
              border: `2px solid ${isMuted ? "#ff4500" : "#4CAF50"}`,
              "&:hover": {
                background: isMuted
                  ? "rgba(255,69,0,0.9)"
                  : "rgba(76,175,80,0.9)",
                transform: "scale(1.1)",
              },
            }}
          >
            <Typography sx={{ fontSize: "18px" }}>
              {isMuted ? "🔇" : "🔊"}
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: "rgba(255,255,255,0.9)",
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            Ad ends in {remainingTime}s
          </Typography>
        </Box>

        <Stack spacing={3} sx={{ px: 4, pb: 4, pt: 2 }}>
          {/* Media - YouTube, Video, Image, or Fallback */}
          {ad.media_url && !mediaError ? (
            <>
              {isYouTubeUrl(ad.media_url) ? (
                // YouTube iframe
                <Box
                  component="iframe"
                  ref={iframeRef}
                  src={getYouTubeEmbedUrl(ad.media_url)}
                  onError={handleMediaError}
                  onLoad={handleMediaLoad}
                  sx={{
                    width: "100%",
                    maxWidth: "500px",
                    height: "300px",
                    border: "none",
                    borderRadius: 2,
                    alignSelf: "center",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.03)",
                    },
                  }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : isVideo(ad.media_url) ? (
                // Regular video
                <Box
                  component="video"
                  ref={videoRef}
                  src={ad.media_url}
                  autoPlay
                  muted={isMuted}
                  loop
                  controls={false}
                  onError={handleMediaError}
                  onLoadedData={handleMediaLoad}
                  sx={{
                    width: "100%",
                    maxWidth: "400px",
                    height: "auto",
                    maxHeight: "300px",
                    objectFit: "cover",
                    borderRadius: 2,
                    alignSelf: "center",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.03)",
                    },
                  }}
                />
              ) : (
                // Image
                <Box
                  component="img"
                  src={ad.media_url}
                  alt={ad.name || "Ad Media"}
                  onError={handleMediaError}
                  onLoad={handleMediaLoad}
                  sx={{
                    width: "100%",
                    maxWidth: "400px",
                    height: "auto",
                    maxHeight: "300px",
                    objectFit: "cover",
                    borderRadius: 2,
                    alignSelf: "center",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.03)",
                    },
                  }}
                />
              )}
            </>
          ) : (
            // Fallback when no media or media failed to load
            <Box
              sx={{
                width: "100%",
                maxWidth: "400px",
                height: "200px",
                backgroundColor: "rgba(255,255,255,0.1)",
                borderRadius: 2,
                alignSelf: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                border: "2px dashed rgba(255,255,255,0.3)",
              }}
            >
              <Typography variant="h4" sx={{ color: "white", mb: 1 }}>
                📺
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)", textAlign: "center" }}
              >
                {mediaError ? "Media failed to load" : "No media available"}
              </Typography>
              {mediaError && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    textAlign: "center",
                    mt: 1,
                    px: 2,
                  }}
                >
                  URL: {ad.media_url}
                </Typography>
              )}
            </Box>
          )}

          {/* Title */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              textAlign: "center",
              color: "white",
              textShadow: "1px 1px 3px rgba(0,0,0,0.3)",
            }}
          >
            {ad.name || "Advertisement"}
          </Typography>

          {/* Description */}
          {ad.description && (
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                maxWidth: "400px",
                alignSelf: "center",
                color: "rgba(255,255,255,0.9)",
                lineHeight: 1.6,
              }}
            >
              {ad.description}
            </Typography>
          )}

          {/* CTA Button */}
          {ad.buttonText && (
            <Box
              sx={{
                alignSelf: "center",
                mt: 2,
                px: 6,
                py: 3,
                backgroundColor: "#4CAF50",
                color: "white",
                borderRadius: 3,
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "16px",
                cursor: "pointer",
                boxShadow: "0 4px 15px rgba(76, 175, 80, 0.3)",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#45a049",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(76, 175, 80, 0.4)",
                },
              }}
            >
              {ad.buttonText}
            </Box>
          )}
        </Stack>
      </Card>
    </Box>
  );
};

const LiveGame = ({ gameId }) => {
  const { username } = useParams();
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [winners, setWinners] = useState([]);
  const [gameStatus, setGameStatus] = useState("completed");
  const [newCall, setNewCall] = useState("");
  const [calledNums, setCalledNums] = useState([]);
  const [ads, setAds] = useState([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [showAd, setShowAd] = useState(false);
  const [lastCalledCount, setLastCalledCount] = useState(0);
  const [previousCall, setPreviousCall] = useState("");

  useEffect(() => {
    document.title = `Game Details | MYBINGO`;
    apiCall({ gameId: gameId });
  }, []);

  useEffect(() => {
    let intervalId;
    let adTimeout;

    const fetchAndSchedule = async () => {
      const callData = await listAllCalledNumbers({ gameId });

      if (callData) {
        const reversedCalledNums = callData?.data?.reverse();
        setCalledNums(reversedCalledNums);

        const currentCalled = callData?.newcall;
        if (currentCalled && currentCalled !== previousCall) {
          setPreviousCall(currentCalled);
          setNewCall(currentCalled);
        }

        if (callData?.ads?.length > 0) {
          setAds(callData.ads);
          setCurrentAdIndex((prev) => (prev + 1) % callData.ads.length);
        }

        // Show Ad first
        setShowAd(true);

        // After 25s, hide Ad and show number
        adTimeout = setTimeout(() => {
          setShowAd(false);
        }, 25000); // show ad for 25 seconds
      }

      // Call your game details API
      try {
        setLoading(true);

        const apiResponse = await unAuthorizedPost("", {
          query: FETCH_A_GAME_DETAILS,
          variables: { gameId },
        });

        if (apiResponse.data.customStatus) {
          const {
            list_a_game_details: { cards, winners },
          } = apiResponse.data.data;

          if (cards?.length) {
            setCards(cards);
            setWinners(winners);
            setGameStatus(cards[0]?.game_id?.status);
          }
        }

        setLoading(false);
      } catch (err) {
        console.log("Error fetching game data", err);
        setLoading(false);
      }
    };

    // Start interval
    fetchAndSchedule(); // initial
    intervalId = setInterval(fetchAndSchedule, 30000); // repeat every 30 seconds

    return () => {
      clearInterval(intervalId);
      clearTimeout(adTimeout);
    };
  }, [gameId]);

  const apiCall = async (requestData) => {
    let callData = await listAllCalledNumbers({ gameId: gameId });
    console.log(callData, "call dataa");

    if (callData) {
      const reversedCalledNums = callData?.data?.reverse();
      setCalledNums(reversedCalledNums);
      setNewCall(callData?.newcall);

      const currentCalledCount = reversedCalledNums?.length || 0;

      // Check for new or same number logic
      if (callData?.newcall) {
        if (callData.newcall !== previousCall) {
          setShowAd(false); // show the number screen
          setPreviousCall(callData.newcall);
        } else {
          if (callData?.ads?.length > 0) {
            setAds(callData.ads);
            setCurrentAdIndex((prev) => (prev + 1) % callData.ads.length);
            setShowAd(true); // show ad again even if it already did before
          }
        }
      }

      // First time setup for ads
      if (
        lastCalledCount === 0 &&
        callData?.ads &&
        Array.isArray(callData.ads)
      ) {
        setAds(callData.ads);
      }

      setLastCalledCount(currentCalledCount);
    }

    try {
      setLoading(true);

      let apiResponse = await unAuthorizedPost("", {
        query: FETCH_A_GAME_DETAILS,
        variables: requestData,
      });

      if (apiResponse.data.customStatus) {
        let {
          list_a_game_details: { cards, winners },
        } = apiResponse.data.data;

        if (cards?.length) {
          setCards(cards);
          console.log(winners, "winnersssssssssssssssss");
          setWinners(winners);
          setGameStatus(cards[0]?.game_id?.status);
        }
        setLoading(false);
        return;
      } else {
        console.log("Failed to fetch data");
        setLoading(false);
      }
    } catch (err) {
      console.log("Unable to get data");
      setLoading(false);
    }
  };

  // Get current ad to display
  const currentAd = ads.length > 0 ? ads[currentAdIndex] : null;

  // Separate full house winner from line winners
  const fullHouseWinner = winners?.find(
    (winner) => winner.line_index === -1 || winner.line_index === "-1"
  );
  const lineWinners = winners?.filter(
    (winner) => winner.line_index !== -1 && winner.line_index !== "-1"
  );

  return (
    <>
      <Loader loading={loading} />

      {/* Ad Pop-up - Show only when there's a new call */}
      {showAd && currentAd && (
        <AdPopup ad={currentAd} onClose={() => setShowAd(false)} />
      )}

      <PageRouteNavigator
        routes={[
          { name: "organisers", path: "/organisers", isActive: false },
          { name: "games", path: `/games/${username}`, isActive: false },
          { name: "Game Details", path: "", isActive: true },
        ]}
      />

      {/* Ad Pop-up - Show only when there's a new call */}
      {showAd && currentAd && (
        <AdPopup ad={currentAd} onClose={() => setShowAd(false)} />
      )}

      <Grid container mt={0.5} mb={5} spacing={2} justifyContent={"center"}>
        {/* Main Content */}
        <Grid item lg={12} md={12} xs={12}>
          <Grid container spacing={2} justifyContent={"center"}>
            {/* Full House Winner Section - Simplified Design */}
            {fullHouseWinner && (
              <>
                <Grid
                  sx={{ color: "white", textAlign: "center" }}
                  item
                  lg={12}
                  xs={12}
                >
                  <b style={{ fontSize: "20px" }}>🏆 Full House Winner</b>
                </Grid>
                <Grid
                  sx={{ color: "white", textAlign: "center" }}
                  item
                  lg={6}
                  md={8}
                  sm={10}
                  xs={12}
                >
                  <Card
                    sx={{
                      p: 2,
                      textAlign: "center",
                      backgroundColor: "#4CAF50",
                      color: "#fff",
                      borderRadius: 3,
                    }}
                  >
                    <Stack
                      direction={"row"}
                      spacing={2}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Avatar
                        src={avatars[6]?.img}
                        name={fullHouseWinner?.winner_id?.username}
                        size={60}
                        round="10px"
                      />
                      <Stack alignItems={"flex-start"}>
                        <b style={{ fontSize: "18px" }}>
                          {fullHouseWinner?.winner_id?.username}
                        </b>
                        <small style={{ opacity: 0.8 }}>
                          {fullHouseWinner?.winner_id?._id}
                        </small>
                        <Chip
                          label="Champion"
                          size="small"
                          sx={{
                            backgroundColor: "#fff",
                            color: "#4CAF50",
                            fontWeight: "bold",
                            mt: 0.5,
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Card>
                </Grid>
              </>
            )}

            {/* Line Winners Section */}
            <Grid
              sx={{ color: "white", textAlign: "center" }}
              item
              lg={12}
              xs={12}
            >
              <b>{fullHouseWinner ? "Line Winners (B-I-N-G-O)" : "Winners"}</b>
            </Grid>

            {lineWinners?.map((winner, index) => (
              <Grid
                sx={{ color: "white", textAlign: "center" }}
                item
                lg={4}
                md={6}
                sm={11}
                xs={12}
                key={index}
              >
                <Card
                  sx={{
                    p: 1,
                    textAlign: "center",
                    backgroundColor: "rgb(97, 134, 255, 0.2)",
                    color: "#fff",
                    borderRadius: 5,
                    opacity: fullHouseWinner ? 0.7 : 1,
                  }}
                >
                  <Stack direction={"row"} spacing={1} alignItems="center">
                    <Card
                      sx={{
                        width: "70px",
                        height: "70px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#a000b5",
                        fontSize: 30,
                        color: "#fff",
                        borderRadius: 4,
                      }}
                    >
                      <b>{BINGO[winner?.line_index]}</b>
                    </Card>
                    <Avatar
                      src={avatars[6]?.img}
                      name={winner?.winner_id?.username}
                      size={50}
                      round="20px"
                    />
                    <Stack alignItems={"flex-start"}>
                      <b className="text-base text-white">
                        {winner?.winner_id?.username}
                      </b>
                      <small style={{ color: "#c9c9c9" }}>
                        {winner?.winner_id?._id}
                      </small>
                    </Stack>
                  </Stack>
                </Card>
              </Grid>
            ))}

            {!winners?.length ? (
              <Grid
                sx={{ color: "white", textAlign: "center" }}
                item
                lg={12}
                xs={12}
              >
                <Card
                  sx={{
                    p: 2,
                    textAlign: "center",
                    backgroundColor: "rgb(97, 134, 255, 0.2)",
                    color: "#fff",
                    borderRadius: 5,
                  }}
                >
                  <p>No winners yet</p>
                </Card>
              </Grid>
            ) : null}

            {/* Full House Winner's Card - Display in separate row */}
            {fullHouseWinner && (
              <>
                <Grid
                  sx={{ color: "white", textAlign: "center" }}
                  item
                  lg={12}
                  xs={12}
                >
                  <b>🏆 Champion's Card</b>
                </Grid>
                {cards
                  .filter(
                    (e) => fullHouseWinner.winner_id._id === e.user_id._id
                  )
                  .map((e, i) => (
                    <Grid
                      item
                      key={`winner-${i}`}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      <Card
                        sx={{
                          p: 2,
                          textAlign: "center",
                          backgroundColor: "rgb(97, 134, 255, 0.2)",
                          borderRadius: 5,
                          border: "2px solid #4CAF50",
                        }}
                      >
                        <Stack spacing={1}>
                          <div
                            style={{
                              textAlign: "center",
                              fontSize: "24px",
                              marginBottom: "5px",
                            }}
                          >
                            👑
                          </div>

                          <Stack>
                            <table
                              id="bingotable"
                              style={{ width: "280px", height: "250px" }}
                            >
                              <tbody>
                                <tr>
                                  <td className="orange">B</td>
                                  {e?.card[0]?.map((num, i) => (
                                    <td
                                      style={{
                                        backgroundColor: isWinningLine(
                                          e?.card[0]
                                        )
                                          ? "#9b59b6"
                                          : "",
                                      }}
                                      id={
                                        num.includes("-marked") ? "marked" : ""
                                      }
                                      key={i}
                                    >
                                      {num?.replace("-marked", "")}
                                    </td>
                                  ))}
                                </tr>
                                <tr>
                                  <td className="orange">I</td>
                                  {e?.card[1]?.map((num, i) => (
                                    <td
                                      style={{
                                        backgroundColor: isWinningLine(
                                          e?.card[1]
                                        )
                                          ? "#9b59b6"
                                          : "",
                                      }}
                                      id={
                                        num.includes("-marked") ? "marked" : ""
                                      }
                                      key={i}
                                    >
                                      {num?.replace("-marked", "")}
                                    </td>
                                  ))}
                                </tr>
                                <tr>
                                  <td className="orange">N</td>
                                  {e?.card[2]?.map((num, i) => (
                                    <td
                                      style={{
                                        backgroundColor: isWinningLine(
                                          e?.card[2]
                                        )
                                          ? "#9b59b6"
                                          : "",
                                      }}
                                      id={
                                        num.includes("-marked") ? "marked" : ""
                                      }
                                      key={i}
                                    >
                                      {num?.replace("-marked", "")}
                                    </td>
                                  ))}
                                </tr>
                                <tr>
                                  <td className="orange">G</td>
                                  {e?.card[3]?.map((num, i) => (
                                    <td
                                      style={{
                                        backgroundColor: isWinningLine(
                                          e?.card[3]
                                        )
                                          ? "#9b59b6"
                                          : "",
                                      }}
                                      id={
                                        num.includes("-marked") ? "marked" : ""
                                      }
                                      key={i}
                                    >
                                      {num?.replace("-marked", "")}
                                    </td>
                                  ))}
                                </tr>
                                <tr>
                                  <td className="orange">O</td>
                                  {e?.card[4]?.map((num, i) => (
                                    <td
                                      style={{
                                        backgroundColor: isWinningLine(
                                          e?.card[4]
                                        )
                                          ? "#9b59b6"
                                          : "",
                                      }}
                                      id={
                                        num.includes("-marked") ? "marked" : ""
                                      }
                                      key={i}
                                    >
                                      {num?.replace("-marked", "")}
                                    </td>
                                  ))}
                                </tr>
                              </tbody>
                            </table>
                          </Stack>

                          <Stack
                            direction={"row"}
                            spacing={2}
                            alignItems="center"
                          >
                            <Avatar
                              src={avatars[6]?.img}
                              name={e?.user_id?.username}
                              size={40}
                              round="20px"
                            />
                            <Stack width={"60%"}>
                              <b className="text-base text-white">
                                {e?.user_id?.username}
                              </b>
                              <small
                                style={{ color: "#4CAF50", fontWeight: "bold" }}
                              >
                                Champion
                              </small>
                            </Stack>
                          </Stack>
                          <button
                            onClick={() =>
                              window.open(
                                `/card/${e?.user_id?.username}?game_id=${gameId}`,
                                "_self"
                              )
                            }
                            className="w-full m-4 bg-gradient-to-r from-green-400 to-pink-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full"
                          >
                            OPEN CARD <i className="fas fa-arrow-right" />
                          </button>
                        </Stack>
                      </Card>
                    </Grid>
                  ))}
              </>
            )}

            {/* Other Players Cards */}
            <Grid
              sx={{ color: "white", textAlign: "center" }}
              item
              lg={12}
              xs={12}
            >
              <b>{fullHouseWinner ? "Other Players" : "Players"}</b>
            </Grid>
            {cards
              .filter(
                (e) =>
                  !fullHouseWinner ||
                  fullHouseWinner.winner_id._id !== e.user_id._id
              )
              .map((e, i) => (
                <Grid item key={i}>
                  <Card
                    sx={{
                      p: 2,
                      textAlign: "center",
                      backgroundColor: "rgb(97, 134, 255, 0.2)",
                      borderRadius: 5,
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack>
                        <table
                          id="bingotable"
                          style={{ width: "280px", height: "250px" }}
                        >
                          <tbody>
                            <tr>
                              <td className="orange">B</td>
                              {e?.card[0]?.map((num, i) => (
                                <td
                                  style={{
                                    backgroundColor: isWinningLine(e?.card[0])
                                      ? "#9b59b6"
                                      : "",
                                  }}
                                  id={num.includes("-marked") ? "marked" : ""}
                                  key={i}
                                >
                                  {num?.replace("-marked", "")}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="orange">I</td>
                              {e?.card[1]?.map((num, i) => (
                                <td
                                  style={{
                                    backgroundColor: isWinningLine(e?.card[1])
                                      ? "#9b59b6"
                                      : "",
                                  }}
                                  id={num.includes("-marked") ? "marked" : ""}
                                  key={i}
                                >
                                  {num?.replace("-marked", "")}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="orange">N</td>
                              {e?.card[2]?.map((num, i) => (
                                <td
                                  style={{
                                    backgroundColor: isWinningLine(e?.card[2])
                                      ? "#9b59b6"
                                      : "",
                                  }}
                                  id={num.includes("-marked") ? "marked" : ""}
                                  key={i}
                                >
                                  {num?.replace("-marked", "")}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="orange">G</td>
                              {e?.card[3]?.map((num, i) => (
                                <td
                                  style={{
                                    backgroundColor: isWinningLine(e?.card[3])
                                      ? "#9b59b6"
                                      : "",
                                  }}
                                  id={num.includes("-marked") ? "marked" : ""}
                                  key={i}
                                >
                                  {num?.replace("-marked", "")}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="orange">O</td>
                              {e?.card[4]?.map((num, i) => (
                                <td
                                  style={{
                                    backgroundColor: isWinningLine(e?.card[4])
                                      ? "#9b59b6"
                                      : "",
                                  }}
                                  id={num.includes("-marked") ? "marked" : ""}
                                  key={i}
                                >
                                  {num?.replace("-marked", "")}
                                </td>
                              ))}
                            </tr>
                          </tbody>
                        </table>
                      </Stack>

                      <Stack direction={"row"} spacing={2} alignItems="center">
                        <Avatar
                          src={avatars[6]?.img}
                          name={e?.user_id?.username}
                          size={40}
                          round="20px"
                        />
                        <Stack width={"60%"}>
                          <b className="text-base text-white">
                            {e?.user_id?.username}
                          </b>
                        </Stack>
                      </Stack>
                      <button
                        onClick={() =>
                          window.open(
                            `/card/${e?.user_id?.username}?game_id=${gameId}`,
                            "_self"
                          )
                        }
                        className="w-full m-4 bg-gradient-to-r from-green-400 to-pink-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full"
                      >
                        OPEN CARD <i className="fas fa-arrow-right" />
                      </button>
                    </Stack>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </Grid>
      </Grid>

      <div className="mainopShadow"></div>
      <div className="fab">
        <div className="mainop">
          <b id="addIcon" className="material-icons">
            {newCall}
          </b>
        </div>
        {calledNums.map((e, i) => (
          <div className="minifab op5" key={i}>
            {e?.num}
          </div>
        ))}
      </div>

      {!cards.length ? (
        <Stack pt={20} color="#fff" alignItems="center">
          No data found
        </Stack>
      ) : null}
    </>
  );
};

export default LiveGame;
